import type {Json} from '@packages/utilities/json';

import * as React from 'react';
import {styled, styledDiv} from '@packages/utilities/styled';
import {showDialog} from '@packages/systems/dialog';
import {Copy, Text} from '@packages/systems/design-system/Text';
import Grid from '@packages/systems/design-system/Grid';

import {FlowTestModal} from './FlowTestModal';
import {
  selectCurrentWebhookTriggerEvents,
  selectRecentWebhookTriggerEvent,
} from '../../selectors';
import {FlowTestStates} from './types';
import {DateTime12} from '@packages/systems/ui/icons';
import {oneLineText} from '@packages/systems/design-system/constants/Styles';
import {useDesignerSelector} from '@packages/systems/designer/flux';

const TEST_ERRORS = {
  noRequestFound: {
    header: 'No request found',
    description:
      'We could not find any requests on the webhook’s URL. Please check your webhook configuration or the request you’ve sent, and try running a test again.',
  },
  missingHeaders: {
    header: 'Missing headers',
    description:
      'Please check your webhook configuration or the request you’ve sent, and try running a test again.',
  },
  invalidJson: {
    header: 'Invalid JSON body',
    description:
      'Please check your webhook configuration or the request you’ve sent, and try running a test again.',
  },
  rateLimitExceeded: {
    header: 'Rate limit exceeded',
    description:
      'There were too many requests within a short period of time. Please try again later.',
  },
  malformedRequest: {
    header: 'Malformed request',
    description:
      'Please check your webhook configuration or the request you’ve sent, and try running a test again.',
  },
} as const;

type ErrorType = keyof typeof TEST_ERRORS;

function parseEvent(
  // @ts-expect-error - TS7006 - Parameter 'event' implicitly has an 'any' type.
  event,
  triggerEvents: any
):
  | null
  | {
      json: Json;
      errorType: null;
    }
  | {
      json: undefined;
      errorType: ErrorType;
    } {
  if (triggerEvents?.length === 0) {
    return {
      json: undefined,
      errorType: 'noRequestFound',
    };
  }

  if (event == null) return null;

  let parsedJson;

  try {
    parsedJson = JSON.parse(event.data as any) as Json;
  } catch (e: any) {
    return {
      json: undefined,
      errorType: 'invalidJson',
    };
  }

  return {
    json: parsedJson,
    errorType: null,
  };
}

function getStatus(
  status: 'idle' | 'inProgress' | 'success' | string,
  resultStatus: 'error' | 'success'
) {
  return status === FlowTestStates.inProgress ? status : resultStatus;
}

function getOutputState(
  parsedEvent:
    | null
    | {
        errorType: null;
        json: Json;
      }
    | {
        errorType: ErrorType;
        json: undefined;
      },
  status: 'idle' | 'inProgress' | 'success' | string
) {
  if (parsedEvent) {
    if (parsedEvent.errorType === 'noRequestFound') {
      if (status === 'inProgress') {
        return {status, json: undefined};
      }

      return {
        status: 'idle',
        idle:
          status === 'success'
            ? {description: 'No available data.'}
            : undefined,
        json: undefined,
      };
    }

    if (parsedEvent.errorType) {
      const {header, description} = TEST_ERRORS[parsedEvent.errorType];
      return {
        status: getStatus(status, FlowTestStates.error),
        error: {
          title: header,
          description,
        },
        json: undefined,
      };
    }

    if (parsedEvent.json !== undefined && status === 'success') {
      return {
        status: getStatus(status, FlowTestStates.success),
        success: {
          description: 'Webhook test has been successful.',
        },
        json: parsedEvent.json,
      };
    }
  }

  return {
    status,
    json: undefined,
  };
}

const dateTimeFormatOptions = {
  month: 'long',
  day: 'numeric',
  year: 'numeric',
  hour: 'numeric',
  minute: 'numeric',
} as const;

function formatTime(time: Date) {
  // Leaving it undefined will use convert to the user's local time format.
  const defaultLocalTimeZone = undefined;

  return new Intl.DateTimeFormat(
    defaultLocalTimeZone,
    dateTimeFormatOptions
  ).format(new Date(time));
}
const TextCopyPill = styledDiv(({theme: {colors, control, gutters}}) => ({
  display: 'grid',
  gridTemplateColumns: `${gutters.x12}px auto`,
  columnGap: gutters.x8,
  alignItems: 'center',
  cursor: 'default',
  background: colors.backgroundVivid,
  paddingLeft: gutters.x12,
  paddingRight: gutters.x12,
  paddingTop: gutters.x8,
  paddingBottom: gutters.x8,
  borderRadius: control.borderRadius,
  border: `1px solid ${colors.borderVivid}`,
  minHeight: gutters.x4 * 8,
}));

const StyledText = styled(Text)(oneLineText, {
  dim: ({theme: {colors}}) => ({
    color: colors.logicFlows.foregroundDimmer,
  }),
});

const NoDataText = styled(Text)({gridColumn: '1/-1'});

const StyledGrid = styled(Grid)(({theme: {gutters}}) => ({
  marginTop: gutters.x16,
}));

const TestWebhookModal = React.memo(({onCancel}: {onCancel: () => void}) => {
  const event = useDesignerSelector(selectRecentWebhookTriggerEvent);
  const triggerEvents = useDesignerSelector(selectCurrentWebhookTriggerEvents);
  const result = parseEvent(event, triggerEvents);
  const outputTestState = getOutputState(result, FlowTestStates.idle);
  const lastRequest = event?.createdOn ? formatTime(event.createdOn) : null;

  return (
    <FlowTestModal
      title="Test incoming webhook trigger"
      input={{
        description: 'We’ll use the last available input data to run the test.',
        content: (
          <StyledGrid gridRowGap={4}>
            <Grid gridRowGap={2}>
              <Copy>Last available data is from:</Copy>
              <TextCopyPill>
                {result?.errorType === 'noRequestFound' ? (
                  <NoDataText>
                    No available data. To run the test, create a webhook and
                    submit sample data.
                  </NoDataText>
                ) : (
                  <>
                    <DateTime12 />
                    <StyledText>{lastRequest}</StyledText>
                  </>
                )}
              </TextCopyPill>
            </Grid>
          </StyledGrid>
        ),
      }}
      output={{
        description:
          'To use data from incoming webhook later in the flow, run a test to retrieve its data schema.',
        // @ts-expect-error - TS2322 - Type '{ status: string; json: undefined; idle?: undefined; error?: undefined; success?: undefined; } | { status: string; idle: { description: string; } | undefined; json: undefined; error?: undefined; success?: undefined; } | { ...; } | { ...; }' is not assignable to type '{ status: "success" | "error" | "idle" | "inProgress"; error?: { title: ReactNode; description: ReactNode; onRetry?: (() => void) | undefined; sideNote?: ReactNode; } | undefined; success?: { ...; } | undefined; idle?: { ...; } | undefined; json: Json | undefined; }'.
        testState: outputTestState,
      }}
      cancel={{
        onClick() {
          onCancel();
        },
      }}
    />
  );
});

/**
 * Custom hook that returns a function that opens the
 * test modal Dialog for the Webhook trigger
 */
export function useTestApplyDataWebhook() {
  return React.useCallback(async function onTestClick() {
    await showDialog(({onCancel}) => <TestWebhookModal onCancel={onCancel} />);
  }, []);
}
